import axios from "axios";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

// Use a relative baseURL so the admin tool stays SAME-ORIGIN regardless of
// where the SPA is served from (preview, emgroupdigital.fr, custom domain…).
// This avoids cross-origin cookie restrictions that would otherwise apply when
// REACT_APP_BACKEND_URL points to a different hostname than the page.
export const adminApi = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  // null = checking, false = anonymous, object = logged in
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/auth/me");
        if (!cancelled) setUser(data);
      } catch (e) {
        if (!cancelled) setUser(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await adminApi.post("/admin/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post("/admin/auth/logout");
    } catch (_) {
      /* ignore */
    }
    setUser(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
